import React, { useState, useEffect, useCallback } from 'react';
import {
  Headphones, AlertCircle, Clock, CheckCircle,
  BarChart3, Plus, RefreshCw, Filter,
} from 'lucide-react';
import {
  PageHeader,
  AppButton,
  SearchInput,
  FilterBar,
  FilterSelect,
  StatsCard,
  Tabs,
  TabsList,
  TabsTrigger,
  Paginator,
} from '../ui';
import TicketTable from './TicketTable';
import ReportIssueModal from './ReportIssueModal';
import CommonToaster from '../Common/CommonToaster';
import apiClient from '../../utils/apiClient';
import { getCurrentUser } from '../../utils/auth';
import './Support.scss';

const statusTabs = [
  { key: '', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'closed', label: 'Closed' },
];

const categories = [
  { value: 'platform_bug', label: 'Platform Bug' },
  { value: 'institute_issue', label: 'Institute Issue' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'other', label: 'Other' },
];

const SupportTicketsPage = ({ myTicketsOnly = false, basePath = '/support/tickets' }) => {
  const user = getCurrentUser();
  const canManage = ['superadmin', 'admin', 'tenantadmin'].includes(user?.usertype);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showModal, setShowModal] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [stats, setStats] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0 });

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize: 15 };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (categoryFilter) params.category = categoryFilter;

      const endpoint = myTicketsOnly ? '/support/my-tickets' : '/support/tickets';
      const { data } = await apiClient.get(endpoint, { params });
      const list = data.tickets || [];
      setTickets(list);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.total || list.length);

      if (data.stats) {
        setStats(data.stats);
      } else if (page === 1 && !statusFilter) {
        const s = { open: 0, in_progress: 0, resolved: 0, closed: 0 };
        list.forEach((t) => { if (s[t.status] !== undefined) s[t.status]++; });
        setStats(s);
      }
    } catch {
      setToast({ show: true, message: 'Failed to load tickets.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, priorityFilter, categoryFilter, myTicketsOnly]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const activeFilterCount = [priorityFilter, categoryFilter].filter(Boolean).length;

  const clearFilters = () => {
    setStatusFilter('');
    setPriorityFilter('');
    setCategoryFilter('');
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const totalAll = (stats.open || 0) + (stats.in_progress || 0) + (stats.resolved || 0) + (stats.closed || 0);

  const getTabCount = (key) => {
    if (!key) return totalAll || totalCount;
    return stats[key] || 0;
  };

  const statusTabItems = statusTabs.map(({ key, label }) => ({
    key,
    label: `${label} (${getTabCount(key)})`,
  }));

  const priorityOptions = [
    { value: '', label: 'All Priorities' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...categories.map((c) => ({ value: c.value, label: c.label })),
  ];

  return (
    <div className="support-page">
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      <PageHeader
        icon={Headphones}
        title={myTicketsOnly ? 'Support' : 'Support Dashboard'}
        description={myTicketsOnly ? 'Track your reported issues and get help' : 'Manage and resolve student support tickets'}
        actions={
          <div className="d-flex align-items-center gap-2">
            <AppButton variant="outline" size="sm" onClick={fetchTickets} disabled={loading} title="Refresh">
              <RefreshCw size={15} className={loading ? 'sp-spin' : ''} />
            </AppButton>
            <AppButton size="sm" className="d-flex align-items-center gap-1" onClick={() => setShowModal(true)}>
              <Plus size={16} /> New Ticket
            </AppButton>
          </div>
        }
      />

      {/* Stats Cards */}
      {!myTicketsOnly && (
        <div className="sp-stats-grid">
          <StatsCard title="Total Tickets" value={totalAll || totalCount} subtitle="All time" icon={Headphones} iconColor="blue" gradient="blue" />
          <StatsCard title="Open" value={stats.open || 0} subtitle="Awaiting response" icon={AlertCircle} iconColor="amber" gradient="amber" />
          <StatsCard title="In Progress" value={stats.in_progress || 0} subtitle="Being worked on" icon={Clock} iconColor="blue" gradient="cyan" />
          <StatsCard title="Resolved" value={stats.resolved || 0} subtitle="Successfully resolved" icon={CheckCircle} iconColor="emerald" gradient="emerald" />
          <StatsCard title="Avg Response" value="-" subtitle="First response time" icon={BarChart3} iconColor="violet" gradient="violet" />
        </div>
      )}

      {/* Student quick stats */}
      {myTicketsOnly && (
        <div className="sp-student-stats">
          {[
            { key: 'open', label: 'Open', dotColor: 'amber' },
            { key: 'in_progress', label: 'In Progress', dotColor: 'blue' },
            { key: 'resolved', label: 'Resolved', dotColor: 'emerald' },
            { key: 'closed', label: 'Closed', dotColor: 'muted' },
          ].map(({ key, label, dotColor }) => (
            <div
              key={key}
              className={`sp-student-stat${statusFilter === key ? ' sp-student-stat--active' : ''}`}
              onClick={() => { setStatusFilter(statusFilter === key ? '' : key); setPage(1); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setStatusFilter(statusFilter === key ? '' : key)}
            >
              <div className="sp-student-stat__dot-row">
                <span className={`sp-student-stat__dot sp-student-stat__dot--${dotColor}`} />
                <span className="sp-student-stat__label">{label}</span>
              </div>
              <span className="sp-student-stat__count">{stats[key] || 0}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs + Filters */}
      <Tabs value={statusFilter} onChange={(value) => { setStatusFilter(value); setPage(1); }}>
        <TabsList>
          {statusTabItems.map((tab) => (
            <TabsTrigger key={tab.key || 'all'} value={tab.key}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <FilterBar
        className="sp-filter-row"
        actions={activeFilterCount > 0 ? (
          <AppButton variant="outline" size="sm" className="sp-filter-clear" onClick={clearFilters}>
            <Filter size={13} /> Clear ({activeFilterCount})
          </AppButton>
        ) : null}
      >
        <SearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder={myTicketsOnly ? 'Search your tickets...' : 'Search tickets, students, or IDs...'}
          className="sp-enterprise-search"
        />
        {!myTicketsOnly && (
          <>
            <FilterSelect
              value={priorityFilter}
              onChange={(value) => { setPriorityFilter(value); setPage(1); }}
              options={priorityOptions}
            />
            <FilterSelect
              value={categoryFilter}
              onChange={(value) => { setCategoryFilter(value); setPage(1); }}
              options={categoryOptions}
            />
          </>
        )}
      </FilterBar>

      {/* Results summary */}
      <div className="sp-results-bar">
        <span className="sp-results-bar__count">
          {loading ? 'Loading...' : `${totalCount} ticket${totalCount !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Ticket List */}
      {!loading && tickets.length === 0 ? (
        <div className="sp-empty">
          <Headphones size={48} strokeWidth={1.2} />
          <h3>No tickets found</h3>
          <p>
            {activeFilterCount > 0 || statusFilter
              ? 'Try adjusting your filters'
              : myTicketsOnly
                ? "You haven't submitted any support tickets yet"
                : 'All clear! No issues reported yet.'}
          </p>
          {(activeFilterCount > 0 || statusFilter) && (
            <AppButton variant="outline" size="sm" onClick={clearFilters}>Clear Filters</AppButton>
          )}
          {myTicketsOnly && !activeFilterCount && !statusFilter && (
            <AppButton variant="outline" size="sm" onClick={() => setShowModal(true)}>
              Create your first ticket
            </AppButton>
          )}
        </div>
      ) : (
        <TicketTable
          tickets={tickets}
          loading={loading}
          basePath={basePath}
          showTenant={false}
          canManage={canManage}
          myTicketsOnly={myTicketsOnly}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="sp-pagination">
          <Paginator
            page={page}
            totalPages={totalPages}
            total={totalCount}
            pageSize={15}
            onChange={setPage}
            infoLabel="tickets"
          />
        </div>
      )}

      <ReportIssueModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          fetchTickets();
        }}
      />
    </div>
  );
};

export default SupportTicketsPage;
